import React, { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../lib/auth';
import FormRenderer from '../components/forms/FormRenderer';

const FIELD_TYPES = [
  { type: 'short_text', label: 'Short Text' },
  { type: 'long_text', label: 'Long Text' },
  { type: 'number', label: 'Number' },
  { type: 'date', label: 'Date' },
  { type: 'time', label: 'Time' },
  { type: 'dropdown', label: 'Dropdown' },
  { type: 'checkbox', label: 'Checkbox' },
  { type: 'signature', label: 'Signature' },
  { type: 'photo', label: 'Photo Upload' },
  { type: 'risk_level', label: 'Risk Level' },
];

const STARTER_TEMPLATES = {
  accident: {
    name: 'Accident Form',
    category: 'Operations',
    description: 'Capture accident details, injuries, witnesses, photos, risk and signatures.',
    fields: [
      { type: 'date', label: 'Accident Date', required: true },
      { type: 'time', label: 'Accident Time', required: true },
      { type: 'short_text', label: 'Site / Location', required: true },
      { type: 'short_text', label: 'Person Involved', required: true },
      { type: 'short_text', label: 'Contact Number', required: false },
      { type: 'long_text', label: 'Description of Accident', required: true },
      { type: 'dropdown', label: 'Injury Sustained?', required: true, options: ['No', 'Yes', 'Unknown'] },
      { type: 'long_text', label: 'Injury Details', required: false },
      { type: 'long_text', label: 'Witness Details', required: false },
      { type: 'dropdown', label: 'Emergency Services Contacted?', required: true, options: ['No', 'Yes'] },
      { type: 'photo', label: 'Photos / Evidence', required: false },
      { type: 'risk_level', label: 'Risk Level', required: true, options: ['Low', 'Medium', 'High', 'Critical'] },
      { type: 'long_text', label: 'Corrective Action Taken', required: false },
      { type: 'signature', label: 'Officer Signature', required: true },
      { type: 'signature', label: 'Supervisor Signature', required: false },
    ],
  },
  incident: {
    name: 'Incident Report',
    category: 'Operations',
    description: 'Capture operational incidents, actions taken, escalation and evidence.',
    fields: [
      { type: 'date', label: 'Incident Date', required: true },
      { type: 'time', label: 'Incident Time', required: true },
      { type: 'short_text', label: 'Client / Site', required: true },
      { type: 'short_text', label: 'Location / Area', required: true },
      { type: 'dropdown', label: 'Incident Type', required: true, options: ['Security', 'Safety', 'Damage', 'Trespass', 'Alarm', 'Other'] },
      { type: 'risk_level', label: 'Severity / Risk Level', required: true, options: ['Low', 'Medium', 'High', 'Critical'] },
      { type: 'long_text', label: 'Incident Details', required: true },
      { type: 'long_text', label: 'Action Taken', required: true },
      { type: 'dropdown', label: 'Police / Emergency Services Contacted?', required: true, options: ['No', 'Yes'] },
      { type: 'dropdown', label: 'Client / Dispatch Notified?', required: true, options: ['No', 'Yes'] },
      { type: 'photo', label: 'Photos / Evidence', required: false },
      { type: 'signature', label: 'Officer Signature', required: true },
    ],
  },
  hazard: {
    name: 'Hazard Report',
    category: 'WHS',
    description: 'Capture hazards, risk rating, immediate controls and follow-up actions.',
    fields: [
      { type: 'date', label: 'Date Identified', required: true },
      { type: 'time', label: 'Time Identified', required: true },
      { type: 'short_text', label: 'Site / Area', required: true },
      { type: 'long_text', label: 'Hazard Description', required: true },
      { type: 'risk_level', label: 'Risk Level', required: true, options: ['Low', 'Medium', 'High', 'Critical'] },
      { type: 'long_text', label: 'Immediate Control Applied', required: false },
      { type: 'dropdown', label: 'Follow Up Required?', required: true, options: ['No', 'Yes'] },
      { type: 'long_text', label: 'Recommended Corrective Action', required: false },
      { type: 'photo', label: 'Photos', required: false },
      { type: 'signature', label: 'Reported By Signature', required: true },
    ],
  },
  vehicle: {
    name: 'Vehicle Inspection',
    category: 'Fleet',
    description: 'Pre-start vehicle inspection for security patrol vehicles.',
    fields: [
      { type: 'date', label: 'Inspection Date', required: true },
      { type: 'time', label: 'Inspection Time', required: true },
      { type: 'short_text', label: 'Vehicle Registration', required: true },
      { type: 'number', label: 'Odometer Reading', required: true },
      { type: 'dropdown', label: 'Tyres Condition', required: true, options: ['Good', 'Monitor', 'Unsafe'] },
      { type: 'dropdown', label: 'Lights Working?', required: true, options: ['Yes', 'No'] },
      { type: 'dropdown', label: 'Brakes OK?', required: true, options: ['Yes', 'No'] },
      { type: 'dropdown', label: 'Damage Observed?', required: true, options: ['No', 'Yes'] },
      { type: 'long_text', label: 'Damage / Defect Details', required: false },
      { type: 'photo', label: 'Vehicle Photos', required: false },
      { type: 'signature', label: 'Officer Signature', required: true },
    ],
  },
  patrol: {
    name: 'Daily Patrol Report',
    category: 'Patrol',
    description: 'General patrol observation and action report.',
    fields: [
      { type: 'date', label: 'Patrol Date', required: true },
      { type: 'time', label: 'Patrol Time', required: true },
      { type: 'short_text', label: 'Site Name', required: true },
      { type: 'short_text', label: 'Area Name', required: true },
      { type: 'long_text', label: 'Activity Observed', required: true },
      { type: 'long_text', label: 'Action Taken', required: true },
      { type: 'dropdown', label: 'Safety Hazard?', required: true, options: ['No', 'Yes'] },
      { type: 'dropdown', label: 'Follow Up Required?', required: true, options: ['No', 'Yes'] },
      { type: 'dropdown', label: 'Emergency Services Contacted?', required: true, options: ['No', 'Yes'] },
      { type: 'photo', label: 'Photos', required: false },
      { type: 'signature', label: 'Officer Signature', required: true },
    ],
  },
};

const makeId = () => `field_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const normaliseField = (field, index = 0) => ({
  id: field.id || makeId(),
  type: field.type || 'short_text',
  label: field.label || `Field ${index + 1}`,
  placeholder: field.placeholder || '',
  help_text: field.help_text || '',
  required: Boolean(field.required),
  options: Array.isArray(field.options) ? field.options : [],
});

const makeField = (type) => {
  const meta = FIELD_TYPES.find(f => f.type === type);
  return normaliseField({
    type,
    label: meta?.label || 'New Field',
    required: false,
    options: type === 'dropdown' ? ['Option 1', 'Option 2'] : type === 'risk_level' ? ['Low', 'Medium', 'High', 'Critical'] : [],
  });
};


function SortableFieldCard({ field, index, selected, onSelect, onMoveUp, onMoveDown, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      className={`rounded-xl border p-4 cursor-pointer transition ${selected ? 'border-blue-500 bg-blue-500/10' : 'border-zinc-800 bg-[#111113] hover:border-zinc-600'}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs text-zinc-500 mb-1">#{index + 1} · {field.type}</div>
          <div className="font-semibold">
            {field.label}
            {field.required ? <span className="text-red-400 ml-1">*</span> : null}
          </div>
          {field.help_text ? <div className="text-xs text-zinc-500 mt-1">{field.help_text}</div> : null}
        </div>

        <div className="flex gap-1 items-center">
          <button
            type="button"
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            className="px-2 py-1 text-xs rounded bg-zinc-800 hover:bg-zinc-700 cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
          >
            ↕
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); onMoveUp(); }} className="px-2 py-1 text-xs rounded bg-zinc-800 hover:bg-zinc-700">↑</button>
          <button type="button" onClick={(e) => { e.stopPropagation(); onMoveDown(); }} className="px-2 py-1 text-xs rounded bg-zinc-800 hover:bg-zinc-700">↓</button>
        </div>
      </div>

      {children}
    </div>
  );
}

export default function FormStudioPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const [activeTab, setActiveTab] = useState('build');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [users, setUsers] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);

  const [template, setTemplate] = useState({
    name: '',
    category: 'Operations',
    description: '',
    status: 'active',
    fields: [],
  });

  const fields = useMemo(
    () => (template.fields || []).map(normaliseField),
    [template.fields]
  );

  const selectedField = fields.find(f => f.id === selectedFieldId) || null;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'assign') {
      setActiveTab('assign');
    }
  }, [location.search]);

  useEffect(() => {
    if (!id || id === 'new') return;

    const loadTemplate = async () => {
      try {
        const res = await api.get(`/forms/templates/${id}`);
        const data = res.data || {};
        setTemplate({
          ...data,
          fields: Array.isArray(data.fields) ? data.fields.map(normaliseField) : [],
        });
      } catch (err) {
        console.error('Failed to load template', err);
        setMessage('Could not load template.');
      }
    };

    loadTemplate();
  }, [id]);

  const updateTemplate = (patch) => {
    setTemplate(prev => ({ ...prev, ...patch }));
  };


  useEffect(() => {
    if (activeTab !== 'assign') return;

    const loadUsers = async () => {
      setAssignLoading(true);
      try {
        const res = await api.get('/users?roles=security_officer,supervisor&is_active=true');
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.users)
            ? res.data.users
            : [];
        setUsers(list);
      } catch (e) {
        console.error('Failed to load users for assignment', e);
        setMessage(e?.response?.data?.detail || 'Could not load staff list.');
      } finally {
        setAssignLoading(false);
      }
    };

    loadUsers();
  }, [activeTab]);

  const toggleAssignedUser = (userId) => {
    const current = Array.isArray(template.assigned_users) ? template.assigned_users : [];
    const next = current.includes(userId)
      ? current.filter(id => id !== userId)
      : [...current, userId];

    updateTemplate({ assigned_users: next });
  };



  const setFields = (nextFields) => {
    setTemplate(prev => ({ ...prev, fields: nextFields.map(normaliseField) }));
  };

  const addField = (type) => {
    const field = makeField(type);
    const next = [...fields, field];
    setFields(next);
    setSelectedFieldId(field.id);
  };

  const addStarterTemplate = (key) => {
    const starter = STARTER_TEMPLATES[key];
    if (!starter) return;

    const nextFields = starter.fields.map(normaliseField);
    setTemplate(prev => ({
      ...prev,
      ...starter,
      fields: nextFields,
    }));
    setSelectedFieldId(nextFields[0]?.id || null);
    setMessage(`${starter.name} starter loaded. You can edit, drag, reorder and save it.`);
  };

  const updateField = (fieldId, patch) => {
    setFields(fields.map(f => f.id === fieldId ? { ...f, ...patch } : f));
  };

  const deleteField = (fieldId) => {
    const next = fields.filter(f => f.id !== fieldId);
    setFields(next);
    if (selectedFieldId === fieldId) {
      setSelectedFieldId(next[0]?.id || null);
    }
  };

  const duplicateField = (fieldId) => {
    const source = fields.find(f => f.id === fieldId);
    if (!source) return;

    const copy = {
      ...source,
      id: makeId(),
      label: `${source.label} Copy`,
    };

    const idx = fields.findIndex(f => f.id === fieldId);
    const next = [...fields];
    next.splice(idx + 1, 0, copy);
    setFields(next);
    setSelectedFieldId(copy.id);
  };

  const moveField = (fieldId, direction) => {
    const idx = fields.findIndex(f => f.id === fieldId);
    if (idx < 0) return;

    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= fields.length) return;

    setFields(arrayMove(fields, idx, target));
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex(f => f.id === active.id);
    const newIndex = fields.findIndex(f => f.id === over.id);

    if (oldIndex < 0 || newIndex < 0) return;
    setFields(arrayMove(fields, oldIndex, newIndex));
  };

  const saveTemplate = async () => {
    if (!template.name?.trim()) {
      setMessage('Template name is required.');
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      const payload = {
        ...template,
        fields,
        status: template.status || 'active',
        category: template.category || 'Operations',
      };

      if (id && id !== 'new') {
        await api.put(`/forms/templates/${id}`, payload);
      } else {
        const res = await api.post('/forms/templates', payload);
        const newId = res.data?.id || res.data?._id;
        if (newId) navigate(`/forms-studio/${newId}`);
      }

      setMessage('Template saved successfully.');
    } catch (err) {
      console.error('Failed to save template', err);
      setMessage(err?.response?.data?.detail || 'Could not save template.');
    } finally {
      setSaving(false);
    }
  };

  const renderFieldPreview = (field) => {
    const baseClass = 'w-full rounded-lg bg-[#09090b] border border-zinc-700 px-3 py-2 text-sm text-zinc-200';

    if (field.type === 'long_text') return <textarea className={baseClass} rows={3} placeholder={field.placeholder || field.label} readOnly />;
    if (field.type === 'date') return <input className={baseClass} type="date" readOnly />;
    if (field.type === 'time') return <input className={baseClass} type="time" readOnly />;
    if (field.type === 'number') return <input className={baseClass} type="number" placeholder={field.placeholder || field.label} readOnly />;
    if (field.type === 'dropdown' || field.type === 'risk_level') {
      return (
        <select className={baseClass} value="" readOnly>
          <option value="">Select...</option>
          {(field.options || []).map(opt => <option key={opt}>{opt}</option>)}
        </select>
      );
    }
    if (field.type === 'checkbox') {
      return <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" readOnly /> Checkbox response</label>;
    }
    if (field.type === 'signature') return <div className="rounded-lg border border-dashed border-zinc-600 p-6 text-center text-sm text-zinc-500">Signature box</div>;
    if (field.type === 'photo') return <div className="rounded-lg border border-dashed border-zinc-600 p-6 text-center text-sm text-zinc-500">Photo upload / evidence capture</div>;

    return <input className={baseClass} placeholder={field.placeholder || field.label} readOnly />;
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-white p-4">
      <div className="flex items-start justify-between mb-8">
        <div>
          <button
            type="button"
            onClick={() => navigate('/forms-studio')}
            className="text-blue-400 hover:text-blue-300 text-sm mb-3"
          >
            ← Back to templates
          </button>
          <h1 className="text-2xl font-bold">{template.name || 'Form Builder'}</h1>
          <p className="text-sm text-zinc-400">Build fields, assign officers, or test the form.</p>
        </div>

        <button
          type="button"
          onClick={saveTemplate}
          disabled={saving}
          className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-sm font-semibold"
        >
          {saving ? 'Saving...' : 'Save Template'}
        </button>
      </div>

      {message ? (
        <div className="mb-4 rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm text-blue-200">
          {message}
        </div>
      ) : null}

      <div className="inline-flex rounded-lg border border-zinc-700 bg-[#18181b] p-1 mb-4">
        {['build', 'assign', 'test'].map(tab => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-md text-sm font-semibold capitalize ${activeTab === tab ? 'bg-blue-600 text-white' : 'text-zinc-300 hover:bg-zinc-800'}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'build' ? (
        <div className="grid grid-cols-12 gap-5">
          <aside className="col-span-12 lg:col-span-3 rounded-2xl bg-[#18181b] border border-zinc-800 p-4">
            <h2 className="font-semibold mb-3">Add Field</h2>

            <div className="mb-4 space-y-2">
              {[
                ['accident', '⚡ Accident Form'],
                ['incident', '🚨 Incident Report'],
                ['hazard', '⚠️ Hazard Report'],
                ['vehicle', '🚗 Vehicle Inspection'],
                ['patrol', '🛡️ Daily Patrol Report'],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => addStarterTemplate(key)}
                  className="w-full rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-left text-sm font-semibold text-emerald-200 hover:bg-emerald-500/20"
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {FIELD_TYPES.map(field => (
                <button
                  key={field.type}
                  type="button"
                  onClick={() => addField(field.type)}
                  className="rounded border border-zinc-700 bg-[#09090b] px-3 py-2 text-left text-sm font-semibold hover:border-blue-500 hover:bg-blue-500/10"
                >
                  + {field.label}
                </button>
              ))}
            </div>
          </aside>

          <main className="col-span-12 lg:col-span-5 rounded-2xl bg-[#18181b] border border-zinc-800 p-4">
            <h2 className="font-semibold mb-4">Template Details</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <input
                className="rounded-lg bg-[#09090b] border border-zinc-800 px-3 py-2 text-sm"
                placeholder="Template name"
                value={template.name || ''}
                onChange={e => updateTemplate({ name: e.target.value })}
              />

              <input
                className="rounded-lg bg-[#09090b] border border-zinc-800 px-3 py-2 text-sm"
                placeholder="Category"
                value={template.category || ''}
                onChange={e => updateTemplate({ category: e.target.value })}
              />
            </div>

            <textarea
              className="w-full rounded-lg bg-[#09090b] border border-zinc-800 px-3 py-2 text-sm mb-5"
              rows={3}
              placeholder="Description"
              value={template.description || ''}
              onChange={e => updateTemplate({ description: e.target.value })}
            />

            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">Fields</h2>
              <span className="text-xs text-zinc-500">{fields.length} fields</span>
            </div>

            {!fields.length ? (
              <div className="rounded-xl border border-dashed border-zinc-700 p-8 text-center text-sm text-zinc-500">
                Add fields from the left panel or load the Accident Form starter.
              </div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-3">
                    {fields.map((field, index) => (
                      <SortableFieldCard
                        key={field.id}
                        field={field}
                        index={index}
                        selected={selectedFieldId === field.id}
                        onSelect={() => setSelectedFieldId(field.id)}
                        onMoveUp={() => moveField(field.id, 'up')}
                        onMoveDown={() => moveField(field.id, 'down')}
                      >
                        {renderFieldPreview(field)}
                      </SortableFieldCard>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </main>

          <aside className="col-span-12 lg:col-span-4 rounded-2xl bg-[#18181b] border border-zinc-800 p-4">
            <h2 className="font-semibold mb-4">Field Settings</h2>

            {!selectedField ? (
              <div className="rounded-xl border border-dashed border-zinc-700 p-8 text-center text-sm text-zinc-500">
                Select a field to edit.
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Label</label>
                  <input
                    className="w-full rounded-lg bg-[#09090b] border border-zinc-700 px-3 py-2 text-sm"
                    value={selectedField.label || ''}
                    onChange={e => updateField(selectedField.id, { label: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Placeholder</label>
                  <input
                    className="w-full rounded-lg bg-[#09090b] border border-zinc-700 px-3 py-2 text-sm"
                    value={selectedField.placeholder || ''}
                    onChange={e => updateField(selectedField.id, { placeholder: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Help Text</label>
                  <input
                    className="w-full rounded-lg bg-[#09090b] border border-zinc-700 px-3 py-2 text-sm"
                    value={selectedField.help_text || ''}
                    onChange={e => updateField(selectedField.id, { help_text: e.target.value })}
                  />
                </div>

                {(selectedField.type === 'dropdown' || selectedField.type === 'risk_level') ? (
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1">Options — one per line</label>
                    <textarea
                      className="w-full rounded-lg bg-[#09090b] border border-zinc-700 px-3 py-2 text-sm"
                      rows={5}
                      value={(selectedField.options || []).join('\n')}
                      onChange={e => updateField(selectedField.id, {
                        options: e.target.value.split('\n').map(x => x.trim()).filter(Boolean),
                      })}
                    />
                  </div>
                ) : null}

                <label className="flex items-center gap-2 text-sm text-zinc-300">
                  <input
                    type="checkbox"
                    checked={Boolean(selectedField.required)}
                    onChange={e => updateField(selectedField.id, { required: e.target.checked })}
                  />
                  Required field
                </label>

                <div className="flex gap-2 pt-3 border-t border-zinc-800">
                  <button
                    type="button"
                    onClick={() => duplicateField(selectedField.id)}
                    className="px-3 py-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm font-semibold"
                  >
                    Duplicate
                  </button>

                  <button
                    type="button"
                    onClick={() => deleteField(selectedField.id)}
                    className="px-3 py-2 rounded-md bg-red-600/20 border border-red-500/40 text-red-200 hover:bg-red-600/30 text-sm font-semibold"
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </aside>
        </div>
      ) : null}

      {activeTab === 'assign' ? (
        <div className="rounded-2xl bg-[#18181b] border border-zinc-800 p-6">
          <div className="flex items-start justify-between gap-3 mb-5">
            <div>
              <h2 className="text-lg font-bold">Assign Template</h2>
              <p className="text-sm text-zinc-400">Select staff who should have access to this form.</p>
            </div>
            <div className="text-sm text-zinc-400">
              Selected: {(template.assigned_users || []).length}
            </div>
          </div>

          {assignLoading ? (
            <div className="text-sm text-zinc-400">Loading staff...</div>
          ) : null}

          {!assignLoading && !users.length ? (
            <div className="rounded-xl border border-dashed border-zinc-700 p-8 text-center text-sm text-zinc-500">
              No active security officers or supervisors found.
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {users.map(user => {
              const userId = user.id || user._id;
              const name = user.full_name || user.name || user.username || user.email || 'Unnamed user';
              const role = user.role || user.position || 'Staff';
              const checked = (template.assigned_users || []).includes(userId);

              return (
                <label
                  key={userId}
                  className={`rounded-xl border p-4 cursor-pointer transition ${checked ? 'border-blue-500 bg-blue-500/10' : 'border-zinc-800 bg-[#111113] hover:border-zinc-600'}`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAssignedUser(userId)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-semibold">{name}</div>
                      <div className="text-xs text-zinc-500">{role}</div>
                      {user.email ? <div className="text-xs text-zinc-500">{user.email}</div> : null}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          <div className="mt-6 flex gap-2">
            <button
              type="button"
              onClick={saveTemplate}
              disabled={saving}
              className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-sm font-semibold"
            >
              {saving ? 'Saving...' : 'Save Assignments'}
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('build')}
              className="px-4 py-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm font-semibold"
            >
              Back to Builder
            </button>
          </div>
        </div>
      ) : null}

      {activeTab === 'test' ? (
        <div className="rounded-2xl bg-[#18181b] border border-zinc-800 p-6">
          <h2 className="font-semibold mb-4">Test Form Preview</h2>
          <FormRenderer template={{ ...template, fields }} readOnly={false} />
        </div>
      ) : null}
    </div>
  );
}
